import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

// Classes `EvalSelected*` vêm daqui; antes chegavam pelo import dos modais do supervisor (redesenhados em 15/10/2026).
import "../evaluation/SelectFreelancersForEvaluationModal.css";

import {
  listStoreRegistrationsApi,
  type ListStoreRegistrationsQuery,
  type StoreRegistrationItem,
} from "../../../views/scheduling/schedulingApi";

import type { SubmittedFreelancerEvaluationPublic } from "../../../views/supervisor/supervisorApi";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function formatTimeFromIso(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatSubmittedAt(iso?: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Props = {
  isOpen: boolean;
  onClose: () => void;

  token: string;

  storeId: string;
  freelancerId: string;

  storeName?: string | null;
  freelancerName?: string | null;
  freelancerCpf?: string | null;

  fetchSubmittedEvaluationsByRegistration: (args: {
    token: string;
    registrationId: string;
  }) => Promise<SubmittedFreelancerEvaluationPublic[]>;
};

const ViewFreelancerSubmittedEvaluationsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  token,
  storeId,
  freelancerId,
  storeName,
  freelancerName,
  freelancerCpf,
  fetchSubmittedEvaluationsByRegistration,
}) => {
  const [startDay, setStartDay] = useState<string>(() => toYmd(new Date()));
  const [endDay, setEndDay] = useState<string>(() => toYmd(new Date()));

  const [loadingReg, setLoadingReg] = useState(false);
  const [loadingEvals, setLoadingEvals] = useState(false);

  const [registration, setRegistration] = useState<StoreRegistrationItem | null>(null);
  const [evaluations, setEvaluations] = useState<SubmittedFreelancerEvaluationPublic[]>([]);

  const busy = loadingReg || loadingEvals;

  const registrationId = useMemo(() => {
    const r: any = registration as any;
    return (r?.id as string | undefined) ?? (r?.registrationId as string | undefined) ?? null;
  }, [registration]);

  const renderAnswer = (a: any) => {
    const q = a?.question;
    if (!q) return "-";
    if (q.type === "BOOLEAN") return a.booleanValue === true ? "Sim" : a.booleanValue === false ? "Não" : "-";
    if (q.type === "SCALE") return a.intValue != null ? String(a.intValue) : "-";
    if (q.type === "TEXT") return typeof a.textValue === "string" && a.textValue.trim() ? a.textValue : "-";
    if (q.type === "SINGLE_CHOICE") {
      const opt = (q.options ?? []).find((o: any) => o.id === a.optionId);
      return opt?.label ?? "-";
    }
    if (q.type === "MULTI_CHOICE") {
      const ids: string[] = Array.isArray(a.optionIds) ? a.optionIds : [];
      const labels = ids
        .map((id) => (q.options ?? []).find((o: any) => o.id === id)?.label)
        .filter(Boolean);
      return labels.length ? labels.join(", ") : "-";
    }
    return "-";
  };

  const loadForPeriod = useCallback(
    async (startDayYmd: string, endDayYmd: string) => {
      if (!token) return;
      if (!storeId) return;
      if (!freelancerId) return;

      setRegistration(null);
      setEvaluations([]);

      try {
        setLoadingReg(true);

        const query: ListStoreRegistrationsQuery = { startDay: startDayYmd, endDay: endDayYmd };
        const regs = await listStoreRegistrationsApi(storeId, token, query);

        const match = (regs ?? [])
          .filter((r: any) => ((r.freelancer as any)?.id ?? r.freelancerId) === freelancerId)
          .sort((a: any, b: any) =>
            String(b?.slot?.startAt ?? b?.day ?? "").localeCompare(String(a?.slot?.startAt ?? a?.day ?? "")),
          )[0];

        if (!match) {
          return;
        }

        setRegistration(match);

        const regId: string | undefined = (match as any)?.id ?? (match as any)?.registrationId;
        if (!regId) {
          toast.error("Não foi possível identificar a inscrição (registrationId).");
          return;
        }

        setLoadingEvals(true);
        const list = await fetchSubmittedEvaluationsByRegistration({ token, registrationId: regId });
        setEvaluations((list ?? []).filter((e) => Boolean(e.submittedAt)));
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao carregar avaliações.");
      } finally {
        setLoadingReg(false);
        setLoadingEvals(false);
      }
    },
    [token, storeId, freelancerId, fetchSubmittedEvaluationsByRegistration],
  );

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, busy]);

  useEffect(() => {
    if (!isOpen) return;

    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      onClose();
      return;
    }
    if (!storeId || !freelancerId) {
      toast.error("Dados insuficientes para abrir avaliações.");
      onClose();
      return;
    }

    const today = toYmd(new Date());
    setStartDay(today);
    setEndDay(today);
    setRegistration(null);
    setEvaluations([]);

    loadForPeriod(today, today);
  }, [isOpen, token, storeId, freelancerId, onClose, loadForPeriod]);

  useEffect(() => {
    if (!isOpen) return;
    loadForPeriod(startDay, endDay);
  }, [isOpen, startDay, endDay, loadForPeriod]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!busy) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="ModalShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        <h2 className="ModalTitle">Avaliações submetidas</h2>

        <div className="ModalBody">
          <div className="Box">
            <div className="BoxTitle">Contexto</div>

            <div className="EvalSelectedBox">
              <div className="EvalSelectedLine">
                <span className="EvalSelectedLabel">Freelancer</span>
                <span className="EvalSelectedValue">{freelancerName ?? freelancerId}</span>
              </div>

              <div className="EvalSelectedLine">
                <span className="EvalSelectedLabel">CPF</span>
                <span className="EvalSelectedValue">{freelancerCpf ?? "-"}</span>
              </div>

              <div className="EvalSelectedLine">
                <span className="EvalSelectedLabel">Loja</span>
                <span className="EvalSelectedValue">{storeName ?? storeId}</span>
              </div>

              <div className="EvalSelectedLine">
                <span className="EvalSelectedLabel">Período</span>
                <span className="EvalSelectedValue">
                  {formatBrDate(startDay)} até {formatBrDate(endDay)}
                </span>
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <label className="freelancer-subtitle" style={{ margin: 0 }}>
                Selecionar período:
              </label>

              <input
                className="freelancer-input"
                type="date"
                value={startDay}
                onChange={(e) => setStartDay(e.target.value)}
                disabled={busy}
              />

              <span className="freelancer-subtitle" style={{ margin: 0 }}>
                até
              </span>

              <input
                className="freelancer-input"
                type="date"
                value={endDay}
                onChange={(e) => setEndDay(e.target.value)}
                disabled={busy}
              />

              <button
                type="button"
                className="PagerButton PagerButtonLight"
                onClick={() => loadForPeriod(startDay, endDay)}
                disabled={busy}
                title="Recarregar"
              >
                Recarregar
              </button>
            </div>
          </div>

          <div className="Box">
            <div className="BoxTitle">Inscrição</div>

            {loadingReg ? (
              <div className="BoxEmpty">Carregando inscrição...</div>
            ) : !registration ? (
              <div className="BoxEmpty">Nenhuma inscrição encontrada para este período.</div>
            ) : (
              <div className="EvalSelectedBox">
                <div className="EvalSelectedLine">
                  <span className="EvalSelectedLabel">ID</span>
                  <span className="EvalSelectedValue">{registrationId ?? "-"}</span>
                </div>

                <div className="EvalSelectedLine">
                  <span className="EvalSelectedLabel">Status</span>
                  <span className="EvalSelectedValue">{(registration as any)?.status ?? "-"}</span>
                </div>

                {(registration as any)?.slot?.startAt ? (
                  <div className="EvalSelectedLine">
                    <span className="EvalSelectedLabel">Horário</span>
                    <span className="EvalSelectedValue">
                      {formatTimeFromIso((registration as any).slot.startAt)} -{" "}
                      {formatTimeFromIso((registration as any).slot.endAt)}
                    </span>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="Box">
            <div className="BoxTitle">Avaliações submetidas</div>

            {loadingEvals ? (
              <div className="BoxEmpty">Carregando avaliações...</div>
            ) : !registration ? (
              <div className="BoxEmpty">Selecione um período com inscrição para ver avaliações.</div>
            ) : evaluations.length === 0 ? (
              <div className="BoxEmpty">Nenhuma avaliação submetida encontrada.</div>
            ) : (
              <div className="DraftList">
                {evaluations.map((ev) => (
                  <div key={ev.id} className="DraftItem" style={{ display: "block" }}>
                    <div className="DraftInfo">
                      <div className="DraftTitle">
                        {ev.form?.formType?.name ? `${ev.form.formType.name}` : "Formulário"} • Enviada em{" "}
                        <b>{formatSubmittedAt(ev.submittedAt)}</b>
                      </div>
                    </div>

                    <div style={{ marginTop: 10 }} />

                    <div className="Box">
                      <div className="BoxTitle">Perguntas e respostas</div>

                      {(ev.answers ?? [])
                        .slice()
                        .sort((a: any, b: any) => ((a.question?.order ?? 0) - (b.question?.order ?? 0)))
                        .map((a: any) => (
                          <div key={a.questionId} className="SelectedInfoBox">
                            <div className="SelectedInfoRow">
                              <span className="SelectedInfoLabel">{a.question?.prompt ?? "-"}</span>
                              <span className="SelectedInfoValue">{renderAnswer(a)}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ModalActions Single">
            <button type="button" className="ModalButton ModalButtonCancel" onClick={onClose} disabled={busy}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewFreelancerSubmittedEvaluationsModal;
